import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Wrench, User, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

const FinTellProject = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const problemPoints = [
    { title: "Time-Intensive Setup", desc: "Building professional websites and developing networking strategies demands substantial time investment that advisors lack" },
    { title: "Forgettable First Impressions", desc: "Paper cards get lost; website URLs require manual typing; contact information sits unused in phone contacts" },
    { title: "Low Engagement Conversion", desc: "Traditional handoffs rarely generate follow-up curiosity or meaningful prospect engagement" },
    { title: "Professional Credibility Gap", desc: "Outdated networking tools signal a lack of innovation, undermining the advisor's expertise perception" },
  ];

  const designGoals = [
    "Intuitive interface requiring minimal learning curve for non-technical advisors",
    "60-second setup time from app download to fully configured digital card",
    "Seamless prospect experience when tapping the NFC wristband",
    "Standalone mobile application with no web dependency",
    "Professional visual design reinforcing financial sector credibility",
  ];

  const features = [
    {
      title: "Real-Time Card Editing",
      description: "Advisors update digital card content instantly without wristband reconfiguration. Changes propagate immediately so prospects always receive current information. This flexibility supports dynamic professional situations—new certifications, service offerings, contact changes—without printing new physical cards.",
    },
    {
      title: "Engagement Analytics Dashboard",
      description: "Performance metrics provide insights into networking effectiveness: Total card taps and views over time, geographic distribution of prospect interactions, peak engagement times and locations informing networking strategy, conversion tracking from card views to scheduled appointments, and comparison benchmarks versus advisor peer averages.",
    },
    {
      title: "Multi-Card Management",
      description: "Advisors create specialized cards for different audience segments—individual investors, business clients, retirement planning prospects. Each card emphasizes relevant expertise and case studies, optimizing messaging for specific demographics.",
    },
    {
      title: "Integration Capabilities",
      description: "Calendar sync for automated appointment scheduling from digital cards, CRM integration logging prospect interactions and follow-up requirements, email marketing platform connections for automated nurture campaigns, and social media profile linking driving multi-channel engagement.",
    },
  ];

  const designPrinciples = [
    {
      title: "Professional Visual Language",
      description: "Interface design balances approachability with financial sector professionalism. Color palette employs sophisticated blues and grays, conveying trustworthiness and stability. Typography selections prioritize readability while maintaining a contemporary aesthetic.",
    },
    {
      title: "Progressive Disclosure",
      description: "The setup flow reveals configuration options sequentially, preventing overwhelming new users with simultaneous decisions. Required fields appear first; optional enhancements surface progressively as core setup completes.",
    },
    {
      title: "Mobile-First Optimization",
      description: "All interfaces are designed for smartphone interaction, acknowledging that advisors primarily use mobile devices during networking events and client meetings. Touch targets exceed minimum size thresholds; gestures follow platform conventions.",
    },
  ];

  const learnings = [
    {
      title: "Novelty as Engagement Driver",
      description: "This project reinforced how novel interaction paradigms create memorable experiences, transcending functional utility. The value of the NFC tap interaction extends beyond information transfer; it sparks curiosity, demonstrates innovation, and creates conversation starters.",
    },
    {
      title: "Constraint-Driven Excellence",
      description: "The 60-second setup constraint forced ruthless prioritization of essential features versus nice-to-have enhancements. This limitation produced superior user experience by preventing feature bloat and maintaining laser focus on core value delivery.",
    },
    {
      title: "Professional vs. Consumer Design Language",
      description: "Financial services applications require a careful balance between approachability and authority. Overly casual design undermines credibility; excessively formal interfaces feel intimidating. The optimal position communicates competence while remaining welcoming.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-6 left-6 z-50"
      >
        <Button
          onClick={() => navigate("/#projects")}
          variant="outline"
          className="no-shiny h-12 px-4 rounded-full border-white/10 bg-black/50 backdrop-blur-xl text-white hover:bg-white/10"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
      </motion.div>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="mb-6 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
              FinTech
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Syne'] text-white mb-6">
              FinTell
            </h1>
            <p className="text-xl md:text-2xl text-white/60 max-w-3xl mb-10">
              Modernizing Financial Advisor Networking Through NFC-Enabled Digital Business Cards
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap gap-6 mb-12">
              <div className="flex items-center gap-2 text-white/50">
                <User className="w-5 h-5" />
                <span>UI/UX Designer</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Calendar className="w-5 h-5" />
                <span>4 Weeks</span>
              </div>
              <div className="flex items-center gap-2 text-white/50">
                <Wrench className="w-5 h-5" />
                <span>Figma, Adobe Suite, Prototyping</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="rounded-3xl overflow-hidden border border-white/5"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_web-creator-900/artifacts/mi9zg57s_Gemini_Generated_Image_uuwh80uuwh80uuwh.png"
              alt="FinTell Platform"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Design Brief */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-emerald-400 tracking-wider uppercase mb-4">
              The Design Brief
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white leading-tight max-w-4xl">
              Design an intuitive mobile application for financial advisors that enables them to manage and expand their professional networks through NFC-enabled smart wristbands.
            </h2>
            <p className="text-lg text-white/60 max-w-3xl mt-6">
              The solution must enable digital business card setup in under 60 seconds while creating engaging, memorable prospect interactions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Problem Space */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-emerald-400 tracking-wider uppercase mb-4">
              Problem Space & Market Context
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              The Financial Advisor Perception Challenge
            </h2>
            <p className="text-lg text-white/60 max-w-4xl leading-relaxed">
              Financial advisors face a fundamental branding problem: they are often perceived as outdated, untrustworthy, or overly aggressive salespeople rather than valuable professional resources. Traditional networking methods—paper business cards, website URLs, WhatsApp numbers—reinforce these negative perceptions by feeling generic, forgettable, and low-effort.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {problemPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{point.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{point.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm font-mono text-emerald-400 tracking-wider uppercase mb-4">
              The Solution
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white mb-6">
              NFC-Powered Digital Business Cards
            </h2>
            <p className="text-lg text-white/60 max-w-3xl mx-auto">
              FinTell transforms financial advisor networking through NFC-enabled wristbands that instantly share digital business cards when tapped against any smartphone. The mobile application manages card content, prospect interactions, and performance analytics while presenting advisors as tech-forward, innovative professionals.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h3 className="text-2xl font-bold font-['Syne'] text-white mb-6">Primary Design Goals</h3>
            <div>
              {designGoals.map((goal, index) => (
                <motion.div
                  key={`designGoals-${index}`}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-start gap-4${index > 0 ? ' mt-4' : ''}`}
                >
                  <div className="w-2 h-2 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
                  <p className="text-white/60">{goal}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-emerald-400 tracking-wider uppercase mb-4">
              Key Application Features
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Core Capabilities
            </h2>
          </motion.div>

          <div>
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-8 rounded-2xl bg-white/5 border border-white/5${index > 0 ? ' mt-8' : ''}`}
              >
                <h3 className="text-xl font-bold font-['Syne'] text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-white/60 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Wireframes */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-emerald-400 tracking-wider uppercase mb-4">
              Design Process
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Medium Fidelity Wireframes
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl overflow-hidden bg-[#121212] border border-white/5"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3Aa69f2e74-06cb-496d-a3ef-f8b9a0df62c3%3AGroup_97.png?table=block&id=2f722ee1-b108-8037-92b1-df2529f60a7d&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
              alt="Medium Fidelity Wireframes"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Design Principles */}
      <section className="py-20 px-6 md:px-12 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-emerald-400 tracking-wider uppercase mb-4">
              Design Principles
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Guiding Philosophy
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {designPrinciples.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{principle.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* High Fidelity */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-emerald-400 tracking-wider uppercase mb-4">
              Final Design
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              High Fidelity Wireframes
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl overflow-hidden bg-[#121212] border border-white/5"
          >
            <img
              src="https://phrygian-tornado-507.notion.site/image/attachment%3A0f910043-0a89-48f5-9bc1-2ececa2fbde1%3AGroup_98.png?table=block&id=2f722ee1-b108-80e4-a146-f7a1d4f0ef6c&spaceId=e955ddb9-8ca5-439c-9295-dc59a3298a61&width=2000&userId=&cache=v2"
              alt="High Fidelity Wireframes"
              className="w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* Reflections */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-b from-[#0d0d0d] to-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="text-sm font-mono text-emerald-400 tracking-wider uppercase mb-4">
              Reflections
            </p>
            <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">
              Design Learnings
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {learnings.map((learning, index) => (
              <motion.div
                key={learning.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-white/5 border border-white/5"
              >
                <h3 className="text-lg font-bold text-white mb-3">{learning.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{learning.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm font-mono text-emerald-400 tracking-wider uppercase mb-4">
              Conclusion
            </p>
            <h2 className="text-2xl md:text-3xl font-bold font-['Syne'] text-white mb-6 leading-relaxed">
              FinTell demonstrates how thoughtful integration of emerging technologies—NFC, mobile-first design, digital business cards—can solve long-standing professional challenges. By transforming forgettable networking moments into memorable digital experiences, the platform helps financial advisors overcome perception barriers while building larger, more engaged prospect networks.
            </h2>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Button
              onClick={() => navigate("/#projects")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Portfolio
            </Button>
            
            <Button
              onClick={() => navigate("/#contact")}
              className="h-12 px-8 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-all"
            >
              Get in Touch
            </Button>
            
            <Button
              onClick={() => navigate("/projects/cryptodalal")}
              variant="outline"
              className="no-shiny h-12 px-6 rounded-full border-white/10 text-white hover:bg-white/5 transition-all"
            >
              Next: CryptoDalal
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinTellProject;
